// The in-progress tasting note the tasting screens build up one step at a time, and the rules for
// turning it into a real StructuredNote once enough of it is filled in.
//
// Quick depth doesn't ask a different SHAPE of note -- packages/domain has exactly one note model --
// it asks fewer questions (aroma families instead of descriptors, one verdict) and fills the rest
// with the most common, defensible default, exactly like a taster who skips a nuance still tastes
// the same wine. The conclusion's criteriaMet is always BACKED INTO from the single verdict the
// taster picks (`criteriaForQuality`, the exact inverse of `deriveQuality`), at both depths.
import {
  HUES_BY_COLOUR,
  QUALITY,
  QUALITY_CRITERION,
  clusterWideId,
  isDescriptorId,
  resolveDescriptor,
  validateNote,
  type Alcohol,
  type AromaIntensity,
  type Body,
  type Clarity,
  type Development,
  type FiveStep,
  type Finish,
  type Hue,
  type Mousse,
  type NoseCondition,
  type Quality,
  type QualityCriterion,
  type Readiness,
  type Sweetness,
  type StructuredNote as DomainNote,
  type VisualIntensity,
  type WineColour,
} from "@proef/domain";
import type { StructuredNote as WireNote } from "@proef/contract";

/**
 * `@proef/contract`'s zod-inferred `StructuredNote` (mutable arrays,
 * `exactOptionalPropertyTypes`-explicit optionals) and `@proef/domain`'s own
 * hand-written interface (readonly arrays) describe the same wire shape but
 * disagree, under TypeScript's strictness, on nothing a runtime ever sees --
 * the edge Worker has the identical adapter (apps/edge/src/note-adapter.ts)
 * for the same reason. `draftToNote` builds the wire shape (what
 * `saveTasting`/`submitFlightNote` actually send); this cast is the one
 * place that lets `validateNote` -- which only exists on the domain's own
 * type -- check it before it's sent.
 */
function toDomainNote(note: WireNote): DomainNote {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return note as unknown as DomainNote;
}

export type NoteDepth = "quick" | "deep";

export interface NoteDraft {
  depth: NoteDepth;
  sparkling: boolean;
  clarity: Clarity;
  intensity: VisualIntensity;
  colour: WineColour;
  hue: Hue;
  appearanceOther: string;
  noseCondition: NoseCondition;
  noseIntensity: AromaIntensity;
  noseDescriptors: string[];
  development: Development;
  sweetness: Sweetness;
  acidity: FiveStep;
  tannin?: FiveStep | undefined;
  alcohol: Alcohol;
  body: Body;
  mousse?: Mousse | undefined;
  flavourIntensity: FiveStep;
  palateDescriptors: string[];
  finish: Finish;
  quality?: Quality | undefined;
  readiness: Readiness;
  freeText: string;
}

/** The inverse of `deriveQuality`: the smallest criteria set that argues
 * for `quality`, so Quick mode's single quality pick still produces a
 * conclusion `validateNote` accepts without asking the taster to itemise
 * criteria they didn't have time to argue. */
export function criteriaForQuality(quality: Quality): QualityCriterion[] {
  const level = QUALITY.indexOf(quality);
  if (level <= 1) return []; // faulty, poor
  return QUALITY_CRITERION.slice(0, level - 1);
}

export function emptyDraft(depth: NoteDepth, colour: WineColour, sparkling: boolean): NoteDraft {
  return {
    depth,
    sparkling,
    clarity: "clear",
    intensity: "medium",
    colour,
    hue:
      HUES_BY_COLOUR[colour][Math.floor(HUES_BY_COLOUR[colour].length / 2)] ??
      HUES_BY_COLOUR[colour][0] ??
      "gold",
    appearanceOther: "",
    noseCondition: "clean",
    noseIntensity: "medium",
    noseDescriptors: [],
    development: "youthful",
    sweetness: "dry",
    acidity: "medium",
    tannin: colour === "red" ? "medium" : undefined,
    alcohol: "medium",
    body: "medium",
    mousse: sparkling ? "creamy" : undefined,
    flavourIntensity: "medium",
    palateDescriptors: [],
    finish: "medium",
    quality: undefined,
    readiness: "drink-now-not-for-ageing",
    freeText: "",
  };
}

/** Builds the real note once `quality` is picked (the last required answer at either depth);
 * `undefined` before that. */
export function draftToNote(draft: NoteDraft): WireNote | undefined {
  let quality: Quality;
  let criteriaMet: QualityCriterion[];
  if (draft.noseCondition === "unclean") {
    quality = "faulty";
    criteriaMet = [];
  } else if (draft.quality === undefined) {
    return undefined;
  } else {
    quality = draft.quality;
    criteriaMet = criteriaForQuality(quality);
  }
  const note: WireNote = {
    schema: "proef.note/1",
    sparkling: draft.sparkling,
    appearance: {
      clarity: draft.clarity,
      intensity: draft.intensity,
      colour: draft.colour,
      hue: draft.hue,
      ...(draft.appearanceOther.trim() !== "" ? { other: draft.appearanceOther.trim() } : {}),
    },
    nose: {
      condition: draft.noseCondition,
      intensity: draft.noseIntensity,
      descriptors: draft.noseDescriptors.filter(isDescriptorId),
      development: draft.development,
    },
    palate: {
      sweetness: draft.sweetness,
      acidity: draft.acidity,
      ...(draft.tannin !== undefined ? { tannin: draft.tannin } : {}),
      alcohol: draft.alcohol,
      body: draft.body,
      ...(draft.mousse !== undefined ? { mousse: draft.mousse } : {}),
      flavourIntensity: draft.flavourIntensity,
      descriptors: draft.palateDescriptors.filter(isDescriptorId),
      finish: draft.finish,
    },
    conclusion: {
      quality,
      criteriaMet,
      readiness: draft.readiness,
    },
    ...(draft.freeText.trim() !== "" ? { freeText: draft.freeText.trim() } : {}),
  };
  return note;
}

/**
 * The inverse of `draftToNote`: the answers of a saved note, back as a draft to edit. Quick notes
 * are stored in the one note shape too, so a note reopened at its own depth reads as it was written.
 */
export function noteToDraft(note: WireNote, depth: NoteDepth): NoteDraft {
  return {
    depth,
    sparkling: note.sparkling,
    clarity: note.appearance.clarity,
    intensity: note.appearance.intensity,
    colour: note.appearance.colour,
    hue: note.appearance.hue,
    appearanceOther: note.appearance.other ?? "",
    noseCondition: note.nose.condition,
    noseIntensity: note.nose.intensity,
    noseDescriptors: [...note.nose.descriptors],
    development: note.nose.development,
    sweetness: note.palate.sweetness,
    acidity: note.palate.acidity,
    tannin: note.palate.tannin,
    alcohol: note.palate.alcohol,
    body: note.palate.body,
    mousse: note.palate.mousse,
    flavourIntensity: note.palate.flavourIntensity,
    palateDescriptors: [...note.palate.descriptors],
    finish: note.palate.finish,
    quality: note.conclusion.quality,
    readiness: note.conclusion.readiness,
    freeText: note.freeText ?? "",
  };
}

/** `validateNote`'s issues, mapped to plain messages a screen can show
 * without knowing packages/domain's path syntax. Empty when the draft
 * (once it has a quality) is coherent. */
export function draftIssues(draft: NoteDraft): string[] {
  const note = draftToNote(draft);
  if (note === undefined) return [];
  return validateNote(toDomainNote(note)).map((i) => i.message);
}

/** Adds or removes a whole aroma family, kept in the list as its cluster-wide descriptor id. */
export function toggleFamily(list: readonly string[], clusterId: string): string[] {
  const id = clusterWideId(clusterId);
  return list.includes(id) ? list.filter((entry) => entry !== id) : [...list, id];
}

/** The families a descriptor list names, in the order they first appear. */
export function familiesIn(descriptors: readonly string[]): string[] {
  const seen = new Set<string>();
  for (const id of descriptors) {
    const resolved = resolveDescriptor(id);
    if (resolved !== undefined) seen.add(resolved.cluster.id);
  }
  return [...seen];
}
